import React, { useState } from "react";
import { NavigateFunction } from "react-router-dom";
import type { MenuItem } from "../MenuData";
import SideMenuTopChrome from "./SideMenuTopChrome";
import SideMenuNavByProduct from "./SideMenuNavByProduct";
import SideMenuFooterBar from "./SideMenuFooterBar";
import AdminCenterPanel, { AdminCenterPanelGroup } from "./AdminCenterPanel";
import SideMenuSkeleton from "./SideMenuSkeleton";
import { NAV_PADDING, SECTION_STACK } from "../sideMenuDensity";

type Auth0ForPartner = {
  isLoading: boolean;
  isAuthenticated: boolean;
  getAccessTokenSilently: () => Promise<string>;
};

export type SideMenuBodyStandardProps = {
  isMobileView: boolean;
  isExpanded: boolean;
  logoSrc: string;
  onLogoError: () => void;
  onToggleExpand: () => void;
  onCloseMobile: () => void;
  productOptions: string[];
  selectedProduct: string;
  onSelectProduct: (value: string) => void;
  tenantType: string;
  pathname: string;
  auth0: Auth0ForPartner;
  internalName: string;
  expandedSections: { [key: string]: boolean };
  setExpandedSections: React.Dispatch<
    React.SetStateAction<{ [key: string]: boolean }>
  >;
  activeItem: string;
  setActiveItem: React.Dispatch<React.SetStateAction<string>>;
  navigate: NavigateFunction;
  computedMainItems: MenuItem[];
  filteredServiceItems: MenuItem[];
  eventManagementItemsForMenu: MenuItem[];
  reflectorV2MenuSections: MenuItem[];
  /** Closing account area, already filtered for this tenant (CTX-512). */
  accountSections: MenuItem[];
  gitbookToken: string | null | undefined;
  /** Whether the account card offers the Admin center row (CTX-583). */
  onOpenHelp: () => void;
  /** Profile still resolving: the tree below would be empty placeholders. */
  isProfileLoading: boolean;
};

/** Props for the Admin center panel that replaces the nav (CTX-583). */
export type SideMenuBodyAdminCenterProps = {
  onBack: () => void;
  onNavigate: (path: string) => void;
  activePath: string;
  groups: AdminCenterPanelGroup[];
  logoSrc: string;
  onLogoError: () => void;
  showDocumentation: boolean;
  onOpenDocumentation: () => void;
  onOpenHelp: () => void;
};

export type SideMenuBodyProps = {
  showAdminCenter: boolean;
  adminCenter: SideMenuBodyAdminCenterProps;
  standard: SideMenuBodyStandardProps;
};

/**
 * The Admin center panel (admins only) vs. the normal sidebar — product chrome,
 * nav tree and footer.
 */
const SideMenuBody: React.FC<SideMenuBodyProps> = ({
  showAdminCenter,
  adminCenter,
  standard: s,
}) => {
  // The chrome and the nav share no border (CTX-335), so a scrolled nav cut
  // its rows off under the tenant card with nothing to say where it ends. A
  // line shows only once the nav has scrolled; transparent before, so it never
  // shifts the rows.
  const [navScrolled, setNavScrolled] = useState(false);

  if (showAdminCenter) {
    return (
      <AdminCenterPanel
        onBack={adminCenter.onBack}
        onNavigate={adminCenter.onNavigate}
        activePath={adminCenter.activePath}
        groups={adminCenter.groups}
        logoSrc={adminCenter.logoSrc}
        onLogoError={adminCenter.onLogoError}
        showDocumentation={adminCenter.showDocumentation}
        onOpenDocumentation={adminCenter.onOpenDocumentation}
        onOpenHelp={adminCenter.onOpenHelp}
      />
    );
  }

  // Until the profile lands, the chrome would read "Select Product" and the
  // nav would be section headers over empty item arrays. Show the rail's shape
  // instead. The footer is profile-independent, so it stays put.
  if (s.isProfileLoading) {
    return (
      <>
        <SideMenuSkeleton
          isExpanded={s.isExpanded}
          isMobileView={s.isMobileView}
          tenantType={s.tenantType}
        />
        <SideMenuFooterBar
          tenantType={s.tenantType}
          isExpanded={s.isExpanded}
          isMobileView={s.isMobileView}
          internalName={s.internalName}
          gitbookToken={s.gitbookToken}
          onOpenHelp={s.onOpenHelp}
        />
      </>
    );
  }

  return (
    <>
      <SideMenuTopChrome
        isMobileView={s.isMobileView}
        isExpanded={s.isExpanded}
        logoSrc={s.logoSrc}
        onLogoError={s.onLogoError}
        onToggleExpand={s.onToggleExpand}
        onCloseMobile={s.onCloseMobile}
        productOptions={s.productOptions}
        selectedProduct={s.selectedProduct}
        onSelectProduct={s.onSelectProduct}
        tenantType={s.tenantType}
        pathname={s.pathname}
        auth0={s.auth0}
      />

      <div
        data-testid="side-menu-nav-scroll"
        onScroll={(e) => setNavScrolled(e.currentTarget.scrollTop > 0)}
        className={`flex-1 overflow-y-auto border-t ${
          navScrolled ? "border-semantic-border-primary" : "border-transparent"
        }`}
      >
        <nav className={`${NAV_PADDING} ${SECTION_STACK}`}>
          <SideMenuNavByProduct
            internalName={s.internalName}
            isExpanded={s.isExpanded}
            isMobileView={s.isMobileView}
            expandedSections={s.expandedSections}
            setExpandedSections={s.setExpandedSections}
            activeItem={s.activeItem}
            setActiveItem={s.setActiveItem}
            navigate={s.navigate}
            computedMainItems={s.computedMainItems}
            filteredServiceItems={s.filteredServiceItems}
            eventManagementItemsForMenu={s.eventManagementItemsForMenu}
            reflectorV2MenuSections={s.reflectorV2MenuSections}
            accountSections={s.accountSections}
            pathname={s.pathname}
          />
        </nav>
      </div>

      <SideMenuFooterBar
        tenantType={s.tenantType}
        isExpanded={s.isExpanded}
        isMobileView={s.isMobileView}
        internalName={s.internalName}
        gitbookToken={s.gitbookToken}
        onOpenHelp={s.onOpenHelp}
      />
    </>
  );
};

export default SideMenuBody;
